import { PoolConfig, PoolToken } from '@trustpad/launchpad';
import { normalizeAddress } from '@trustpad/launchpad/dist/shared/utils';
import Link from 'next/link';
import * as React from 'react';
import Skeleton from 'react-loading-skeleton';
import { toBN } from 'web3-utils';
import { usePriceCoinGecko } from '~common/hooks/usePriceCoinGecko';
import { DHDistribution } from '~features/diamondHands/useDHDistribution';
import { DHReward } from '~features/diamondHands/useDHTreasury';
import { useServerCachedTokenPrice } from '~features/price';
import { fNum, normalizeDecimals, toEthN } from '~utils';

export function DiamondHandsTotalStats({
  rewards,
  tokenPools,
  distribution,
}: {
  rewards: DHReward[];
  tokenPools: Record<string, PoolConfig>;
  distribution: DHDistribution;
}) {
  return (
    <div className="bg-dark3 -mx-5 p-5">
      <table className="w-full ">
        <thead>
          <tr>
            <td>Token</td>
            <td>Total collected rewards</td>
            {false && <td>Total payout</td>}
          </tr>
        </thead>
        <tbody>
          {rewards.map((t) => {
            const { id, token, sale } = tokenPools[t.token];
            const { imageSrc, name, symbol } = token;
            const isRetroSale =
              distribution.isRetroSale[
                distribution.sales.indexOf(sale.address)
              ];
            return (
              <tr key={t.token} className="border-b border-gray-800 ">
                <td className="flex items-center gap-2 py-3 text-base">
                  {imageSrc && (
                    <div className="h-12 w-12 rounded-full inline-flex items-center mr-1">
                      <img
                        src={imageSrc}
                        alt={symbol}
                        className="rounded-full"
                      />
                    </div>
                  )}
                  <span>
                    <Link href={`/pool/${id}`} className="no-underline">
                      {name}
                    </Link>
                    {isRetroSale && (
                      <span className="text-xs text-secondary"> (retro)</span>
                    )}
                  </span>
                </td>
                <td className="text-xl">
                  <DollarAmount amount={t.total} token={token} showTokens />{' '}
                </td>
                {false && (
                  <td>
                    <DollarAmount amount={t.paid} token={token} />
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>

      {false && <pre>{JSON.stringify({ rewards }, null, 2)}</pre>}
    </div>
  );
}

export function DollarAmount({
  amount,
  token,
  showTokens,
}: {
  amount: number;
  token: PoolToken;
  showTokens?: boolean;
}) {
  const { prices, isLoading } = useServerCachedTokenPrice({
    tokenAddresses: [token.address],
  });
  const price = prices[normalizeAddress(token.address)]?.price;
  const usdValue = amount * price;

  if (isLoading) return <Skeleton width={20} className="inline-block" />;

  return (
    <>
      {price ? (
        <>
          <span className="brand-text">${fNum(usdValue, 0)}</span>
          {showTokens && (
            <span className="text-sm text-secondary ml-1">
              {fNum(amount, 0)} {token.symbol}
            </span>
          )}
        </>
      ) : (
        <span className="brand-text">
          {fNum(amount)} {token.symbol}
        </span>
      )}
    </>
  );
}

function TokenRewardNumber({
  token,
  amount,
}: {
  token: PoolToken;
  amount: number;
}) {
  const prices = usePriceCoinGecko(token.coingeckoId);
  const { imageSrc, symbol } = token;
  const usdValue = amount * prices?.price;

  return (
    <span className="inline-flex items-center px-1">
      {imageSrc && (
        <div className="h-8 w-8 rounded-full inline-flex items-center mr-1">
          <img src={imageSrc} alt={symbol} className="rounded-full" />
        </div>
      )}
      <div>
        {fNum(amount, 0)} {symbol}
        {prices?.price ? ` [$${fNum(usdValue, 0)}]` : ''}
      </div>
    </span>
  );
}

function TokenReward({ token, amount }: { token: PoolToken; amount: string }) {
  const prices = usePriceCoinGecko(token.coingeckoId);
  const { imageSrc, symbol } = token;
  const rewardBN = toBN(amount);
  const usdValue =
    toEthN(normalizeDecimals(rewardBN, token.decimals)) * prices?.price;

  return (
    <span className="inline-flex items-center px-1">
      {imageSrc && (
        <div className="h-8 w-8 rounded-full inline-flex items-center mr-1">
          <img src={imageSrc} alt={symbol} className="rounded-full" />
        </div>
      )}
      <div>
        {fNum(rewardBN, 0)} {symbol}
        {prices?.price ? ` [$${fNum(usdValue, 0)}]` : ''}
      </div>
    </span>
  );
}
